// src/pages/plantation/monitor-setara-urea-per-umur/FilterPanelMonitorSetaraUrea.jsx
import React, { useState } from "react";
import {
    Paper,
    Collapse,
    Stack,
    Chip,
    Menu,
    MenuItem,
    Box
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PlaceIcon from "@mui/icons-material/Place";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import ParkIcon from "@mui/icons-material/Park";
import ClassIcon from "@mui/icons-material/Class";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';

// Import styles dari file terpisah (akan kita buat nanti)
import { getFilterChipStyles, getResetChipStyles } from "./FilterChipStylesMonitorSetaraUrea";
import { menuStyles } from "./MenuStylesMonitorSetaraUrea";

function FilterPanelMonitorSetaraUrea({
    filterOpen,
    paperShadow,
    selectedFilters,
    updateFilter,
    clearAllFilters,
    filterOptions // Opsi filter yang akan di-pass dari parent
}) {
    // State untuk menu dropdown masing-masing filter
    const [anchorEls, setAnchorEls] = useState({
        statusLokasi: null,
        kodeWilayah: null,
        wilayahPlantCare: null,
        jenisBibit: null,
        kelasBibit: null
    });

    // Handler untuk membuka menu dropdown
    const handleMenuOpen = (event, filterKey) => {
        setAnchorEls({
            ...anchorEls,
            [filterKey]: event.currentTarget
        });
    };

    // Handler untuk menutup menu dropdown
    const handleMenuClose = (filterKey) => {
        setAnchorEls({
            ...anchorEls,
            [filterKey]: null
        });
    };

    // Handler untuk memilih item dari dropdown
    const handleFilterSelect = (filterKey, value, label) => {
        updateFilter(filterKey, { value, label });
        handleMenuClose(filterKey);
        console.log(`Filter ${filterKey} dipilih:`, { value, label });
    };

    // Handler untuk menghapus filter yang dipilih
    const handleFilterRemove = (filterKey) => {
        updateFilter(filterKey, null);
        console.log(`Filter ${filterKey} dihapus`);
    };

    // Handler untuk menghapus semua filter
    const handleClearAllFiltersLocal = () => {
        clearAllFilters();
        console.log("Semua filter dihapus");
    };

    // Fungsi untuk mendapatkan deleteIcon berdasarkan status filter
    const getDeleteIcon = (filterKey) => {
        const isOpen = Boolean(anchorEls[filterKey]);
        const hasSelection = selectedFilters[filterKey] !== null;
        
        if (hasSelection) {
            return <CloseIcon />;
        }
        
        return (
            <ArrowDropDownIcon
                sx={{
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                }}
            />
        );
    };

    // Handler untuk klik delete icon
    const handleDeleteIconClick = (e, filterKey) => {
        e.stopPropagation(); // Mencegah event bubble ke onClick Chip
        
        if (selectedFilters[filterKey]) {
            // Jika ada pilihan, hapus pilihan
            handleFilterRemove(filterKey);
        } else {
            // Jika tidak ada pilihan, buka/tutup menu
            handleMenuOpen(e, filterKey);
        }
    };

    // Handler untuk klik Chip
    const handleChipClick = (e, filterKey) => {
        // Jika Chip diklik, selalu buka menu
        handleMenuOpen(e, filterKey);
    };

    return (
        <Collapse 
            in={filterOpen}
            timeout={300}
            easing={{enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)'}}
            sx={{ width: '100%', overflow: 'hidden', mb: 0 }}
        >
            <Paper
                sx={{
                    p: 0,
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: 0,
                    transition: 'all 0.3s ease',
                    backgroundColor: "transparent"
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        {/* Chip Status Lokasi */}
                        <Box>
                            <Chip
                                title="Status Lokasi"
                                icon={<PlaceIcon />}
                                label={selectedFilters.statusLokasi ? selectedFilters.statusLokasi.label : "Status Lokasi"}
                                deleteIcon={getDeleteIcon('statusLokasi')}
                                onDelete={(e) => handleDeleteIconClick(e, 'statusLokasi')}
                                onClick={(e) => handleChipClick(e, 'statusLokasi')}
                                color={selectedFilters.statusLokasi ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.statusLokasi, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.statusLokasi}
                                open={Boolean(anchorEls.statusLokasi)}
                                onClose={() => handleMenuClose('statusLokasi')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.statusLokasi.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('statusLokasi', option.value, option.label)}
                                        selected={selectedFilters.statusLokasi?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Chip Kode Wilayah */}
                        <Box>
                            <Chip
                                title="Kode Wilayah"
                                icon={<LocationOnIcon />}
                                label={selectedFilters.kodeWilayah ? selectedFilters.kodeWilayah.label : "Kode Wilayah"}
                                deleteIcon={getDeleteIcon('kodeWilayah')}
                                onDelete={(e) => handleDeleteIconClick(e, 'kodeWilayah')}
                                onClick={(e) => handleChipClick(e, 'kodeWilayah')}
                                color={selectedFilters.kodeWilayah ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.kodeWilayah, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.kodeWilayah}
                                open={Boolean(anchorEls.kodeWilayah)}
                                onClose={() => handleMenuClose('kodeWilayah')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.kodeWilayah.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('kodeWilayah', option.value, option.label)}
                                        selected={selectedFilters.kodeWilayah?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Chip Wilayah Plant Care */}
                        <Box>
                            <Chip
                                title="Wilayah Plant Care"
                                icon={<PersonIcon />}
                                label={selectedFilters.wilayahPlantCare ? selectedFilters.wilayahPlantCare.label : "Wilayah Plant Care"}
                                deleteIcon={getDeleteIcon('wilayahPlantCare')}
                                onDelete={(e) => handleDeleteIconClick(e, 'wilayahPlantCare')}
                                onClick={(e) => handleChipClick(e, 'wilayahPlantCare')}
                                color={selectedFilters.wilayahPlantCare ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.wilayahPlantCare, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.wilayahPlantCare}
                                open={Boolean(anchorEls.wilayahPlantCare)}
                                onClose={() => handleMenuClose('wilayahPlantCare')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.wilayahPlantCare.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('wilayahPlantCare', option.value, option.label)}
                                        selected={selectedFilters.wilayahPlantCare?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Chip Jenis Bibit */}
                        <Box>
                            <Chip
                                title="Jenis Bibit"
                                icon={<ParkIcon />}
                                label={selectedFilters.jenisBibit ? selectedFilters.jenisBibit.label : "Jenis Bibit"}
                                deleteIcon={getDeleteIcon('jenisBibit')}
                                onDelete={(e) => handleDeleteIconClick(e, 'jenisBibit')}
                                onClick={(e) => handleChipClick(e, 'jenisBibit')}
                                color={selectedFilters.jenisBibit ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.jenisBibit, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.jenisBibit}
                                open={Boolean(anchorEls.jenisBibit)}
                                onClose={() => handleMenuClose('jenisBibit')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.jenisBibit.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('jenisBibit', option.value, option.label)}
                                        selected={selectedFilters.jenisBibit?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Chip Kelas Bibit */}
                        <Box>
                            <Chip
                                title="Kelas Bibit"
                                icon={<ClassIcon />}
                                label={selectedFilters.kelasBibit ? selectedFilters.kelasBibit.label : "Kelas Bibit"}
                                deleteIcon={getDeleteIcon('kelasBibit')}
                                onDelete={(e) => handleDeleteIconClick(e, 'kelasBibit')}
                                onClick={(e) => handleChipClick(e, 'kelasBibit')}
                                color={selectedFilters.kelasBibit ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.kelasBibit, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.kelasBibit}
                                open={Boolean(anchorEls.kelasBibit)}
                                onClose={() => handleMenuClose('kelasBibit')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.kelasBibit.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('kelasBibit', option.value, option.label)}
                                        selected={selectedFilters.kelasBibit?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Stack>
                    
                    {/* Reset Filter Chip - tampilkan hanya jika ada filter yang aktif */}
                    {Object.values(selectedFilters).some(filter => filter !== null) && (
                        <Chip
                            icon={<CancelOutlinedIcon style={{ color: 'white' }} />}
                            label="Reset Filter"
                            onClick={handleClearAllFiltersLocal}
                            sx={getResetChipStyles(paperShadow)}
                        />
                    )}
                </Stack>
            </Paper>
        </Collapse>
    );
}

export default FilterPanelMonitorSetaraUrea;